import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { generateAuditedClient } from "../audit/auditedClient";
import { isAuthorizationError } from "../audit/graphqlErrors";
import {
  bucketSlices,
  buildDailySeries,
  fillDailyTrend,
  filterOperationEntries,
  sliceShare,
  windowStartDay,
  DailyEntry,
  OperationTypeFilter,
  Slice,
  TrendPoint,
  UsageEntry,
} from "../audit/usageChart";
import { getUsageReport } from "../graphql/queries";
import {
  GetUsageReportInput,
  UsageReport,
  UsageReportEntry,
} from "../models/API";

/**
 * Usage Report Viewer (audit-usage-report spec, Requirements 1, 2, 3).
 *
 * A superuser-only surface, rendered as a tab inside `AuditLogViewer`, that
 * shows application usage as a pie chart over the pre-aggregated
 * `daily_usage_summary` rows via the `getUsageReport` query. Mirrors the
 * conventions of its sibling `AuditLogViewer` (shared audited Amplify client,
 * shared `isAuthorizationError` classifier, MUI layout); the server pipeline
 * resolver remains the authoritative access gate.
 *
 * Behavior:
 * - On open (and whenever the dimension or window selection changes while
 *   active), issue exactly one `getUsageReport` retrieval carrying the current
 *   selections; defaults are dimension `product` / 30 days (Req 1.3, 3.3,
 *   3.4).
 * - Exclusive selection controls offer exactly
 *   product/user/team/operation/channel and 7/30/90 days, always holding one
 *   indicated selection; they stay interactive during loading and after
 *   generic errors, and changed selections are retained on error (Req 3.1,
 *   3.2, 1.7, 3.7). Product answers WHAT feature was used (Ask AI,
 *   Knowledge, ...); channel answers WHERE the request came from (web,
 *   finn-mcp, slack, backend) — orthogonal axes, e.g. a finn-mcp ask-AI call
 *   is product "Ask AI" on channel "finn-mcp".
 * - A retrieval in progress shows a loading indicator (Req 1.4); a retrieval
 *   that does not complete within 30 seconds is treated as failed (Req 1.9).
 * - Completions (success or failure) are applied only when they belong to the
 *   latest issued request, so a stale response never replaces the display for
 *   the current selections (Req 3.5, 3.6).
 * - Authorization error -> only an access-denied message (no chart, no
 *   controls) (Req 1.6). Other errors -> retrieval-failed message in place of
 *   the chart, controls kept for retry (Req 1.7).
 * - `data_as_of` present -> "Data as of YYYY-MM-DD (UTC)" caption (Req 1.5);
 *   null -> "no aggregated usage data yet" indication in its place (Req 1.8);
 *   zero entries -> empty-state message without a chart (Req 2.6).
 * - Otherwise a Recharts pie over `bucketSlices(entries)` (top-10 + "Other")
 *   with counts as slice values so sizing is proportional, plus a legend and
 *   tooltip showing each slice's label, count, and one-decimal percentage
 *   share (Req 2.3, 2.4).
 * - The Operation view adds a type sub-selection (All / Queries /
 *   Mutations): a client-side filter over the already-retrieved entries by
 *   their "(query)" / "(mutation)" value suffix — no refetch. Under a type
 *   filter the suffix is stripped from slice labels and the top-10 +
 *   "Other" bucketing applies within the filtered set.
 * - The User view additionally shows the distinct-users-per-day trend line
 *   over the same window (`daily_distinct_users`, zero-filled across days
 *   without usage via `fillDailyTrend`); service identities are excluded
 *   server-side, so the line counts people. The trend belongs with the
 *   user slicing rather than repeating under every dimension.
 * - Every other view additionally shows an operations-per-day line chart
 *   with one line per dimension value (`daily_entries`, pivoted and
 *   zero-filled via `buildDailySeries`). The server keeps the top 10 values
 *   by window total — the pie's individual slices — and folds the rest into
 *   one "Other" line, so the legend reads like the pie's. On the Operation
 *   view the type sub-selection applies to the lines as well (same
 *   client-side filter); the "Other" line cannot be split by type, so it is
 *   drawn under "All" only.
 */

/** Selectable slicing dimensions (Req 3.1). */
type UsageDimension = "product" | "user" | "team" | "operation" | "channel";

/** Selectable reporting windows in days (Req 3.2). */
type WindowDays = 7 | 30 | 90;

/** Retrieval outcome driving the normal, loading, denied, and error UIs. */
type ViewerStatus = "ok" | "loading" | "auth-error" | "error";

/**
 * The applied result of the latest matching retrieval.
 * `dailyEntries` / `dailyDistinctUsers` are null when the response carried
 * no such field (an older backend during a rolling deploy) — the matching
 * line chart is then omitted rather than drawn as misleading all-zero lines.
 */
type ReportData = {
  dataAsOf: string | null;
  entries: UsageReportEntry[];
  dailyEntries: DailyEntry[] | null;
  dailyDistinctUsers: TrendPoint[] | null;
};

/** Human-readable dimension names for the daily chart's title and caption. */
const DIMENSION_LABELS: Record<UsageDimension, string> = {
  product: "product",
  user: "user",
  team: "team",
  operation: "operation",
  channel: "channel",
};

/** Default selections applied when the viewer opens (Req 3.3). */
const DEFAULT_DIMENSION: UsageDimension = "product";
const DEFAULT_WINDOW_DAYS: WindowDays = 30;

/** Retrieval deadline; exceeding it is treated as a failure (Req 1.9). */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Fixed palette: one color per individual slice (up to `MAX_SLICES = 10`)
 * plus one for the "Other" bucket, so a fully bucketed chart never repeats.
 */
const SLICE_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#aec7e8",
];

/**
 * Reject when `promise` does not settle within `ms` milliseconds (Req 1.9).
 * The caller treats the timeout rejection as a generic retrieval failure. A
 * late settlement of the underlying request is inert: the returned promise has
 * already settled, and the request-id guard would ignore it anyway.
 */
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () =>
        reject(new Error(`Usage report retrieval timed out after ${ms} ms`)),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (reason) => {
        clearTimeout(timer);
        reject(reason);
      }
    );
  });

/** "count (share%)" detail shown in the legend and tooltip (Req 2.4). */
const formatSliceDetail = (count: number, total: number): string =>
  `${count} (${sliceShare(count, total).toFixed(1)}%)`;

interface UsageReportViewerProps {
  /**
   * Whether the viewer is the active (visible) tab. While inactive no
   * retrievals are issued; each activation or selection change while active
   * triggers exactly one retrieval. Defaults to true.
   */
  isActive?: boolean;
}

export const UsageReportViewer: React.FC<UsageReportViewerProps> = ({
  isActive = true,
}) => {
  const client = useMemo(() => generateAuditedClient(), []);

  // Selections (Req 3.1, 3.2) with the opening defaults (Req 3.3).
  const [dimension, setDimension] = useState<UsageDimension>(DEFAULT_DIMENSION);
  const [windowDays, setWindowDays] = useState<WindowDays>(DEFAULT_WINDOW_DAYS);
  // Operation-type sub-selection (client-side filter; no refetch).
  const [operationType, setOperationType] =
    useState<OperationTypeFilter>("all");

  // Result + status. The first fetch starts on mount, hence initial "loading".
  const [status, setStatus] = useState<ViewerStatus>("loading");
  const [report, setReport] = useState<ReportData | null>(null);

  // Monotonic id of the most recently issued retrieval. A completion (success
  // OR failure) is applied only when its captured id still equals this value,
  // so responses for superseded selections or timed-out requests are ignored
  // (Req 3.5, 3.6, 1.9).
  const latestRequestId = useRef(0);

  // Exactly one retrieval per activation or selection change (Req 1.3, 3.4).
  useEffect(() => {
    if (!isActive) return;
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setStatus("loading");
    const input: GetUsageReportInput = {
      dimension,
      window_days: windowDays,
    };
    withTimeout(
      client.graphql({ query: getUsageReport, variables: { input } }),
      REQUEST_TIMEOUT_MS
    ).then(
      (result) => {
        if (requestId !== latestRequestId.current) return; // stale (Req 3.6)
        const usageReport: UsageReport | null =
          (result as any)?.data?.getUsageReport ?? null;
        setReport({
          dataAsOf: usageReport?.data_as_of ?? null,
          entries: usageReport?.entries ?? [],
          dailyEntries: usageReport?.daily_entries ?? null,
          dailyDistinctUsers: usageReport?.daily_distinct_users ?? null,
        });
        setStatus("ok");
      },
      (err) => {
        if (requestId !== latestRequestId.current) return; // stale (Req 3.6)
        console.error("Failed to retrieve usage report:", err);
        // Selections are state, so the changed values stay indicated by the
        // controls for retry (Req 3.7).
        setReport(null);
        setStatus(isAuthorizationError(err) ? "auth-error" : "error");
      }
    );
  }, [isActive, dimension, windowDays, client]);

  /** Keep exactly one option selected: ignore MUI's deselection null. */
  const handleDimensionChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: UsageDimension | null
  ) => {
    if (value !== null) setDimension(value);
  };

  const handleWindowChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: WindowDays | null
  ) => {
    if (value !== null) setWindowDays(value);
  };

  const handleOperationTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: OperationTypeFilter | null
  ) => {
    if (value !== null) setOperationType(value);
  };

  if (status === "auth-error") {
    // Req 1.6: render ONLY the access-denied message; no chart, no controls.
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          You do not have access to the usage report.
        </Alert>
      </Box>
    );
  }

  // Accepts the pure chart entry shape: the wire `UsageReportEntry` is
  // structurally assignable, and the operation view passes filtered entries.
  const renderChart = (entries: UsageEntry[]) => {
    const slices: Slice[] = bucketSlices(entries);
    // Bucketing conserves the total, so shares are fractions of the retrieved
    // total operation count (Req 2.3, 2.5).
    const total = slices.reduce((sum, slice) => sum + slice.count, 0);
    return (
      <Paper sx={{ p: 2 }}>
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={slices}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius={130}
              isAnimationActive={false}
            >
              {slices.map((slice, index) => (
                <Cell
                  key={`${slice.label}-${index}`}
                  fill={SLICE_COLORS[index % SLICE_COLORS.length]}
                />
              ))}
            </Pie>
            {/* Req 2.4: label, count, and one-decimal share per slice. */}
            <Tooltip
              formatter={(
                value: number | string | Array<number | string>,
                name: number | string
              ) => [
                formatSliceDetail(
                  typeof value === "number" ? value : Number(value),
                  total
                ),
                String(name),
              ]}
            />
            <Legend
              formatter={(label: React.ReactNode, entry: any) => {
                const count = Number(
                  entry?.payload?.count ?? entry?.payload?.payload?.count ?? 0
                );
                return `${String(label)}: ${formatSliceDetail(count, total)}`;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
    );
  };

  /**
   * Distinct-users-per-day trend line over the current reporting window,
   * rendered on the User view only (unique people per day belongs with the
   * user slicing). Zero-filled so days without usage show as 0 rather than
   * being skipped.
   */
  const renderTrend = (dataAsOf: string, points: TrendPoint[] | null) => {
    if (points === null) return null; // pre-trend backend (rolling deploy)
    const trend = fillDailyTrend(
      points,
      windowStartDay(dataAsOf, windowDays),
      dataAsOf
    );
    if (trend.length === 0) return null;
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Distinct users per day
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Unique people using FINN each day over the selected window (service
          identities excluded).
        </Typography>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart
            data={trend}
            margin={{ top: 16, right: 24, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} minTickGap={24} />
            <YAxis allowDecimals={false} width={44} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value: number | string | Array<number | string>) => [
                String(value),
                "Distinct users",
              ]}
            />
            <Line
              type="monotone"
              dataKey="distinct_users"
              name="Distinct users"
              stroke={SLICE_COLORS[0]}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>
    );
  };

  /**
   * Operations-per-day lines, one per dimension value, over the current
   * reporting window — rendered on every view but User (which charts
   * distinct people instead). The server already keeps the top 10 values
   * by window total plus an "Other" bucket, so the lines are the pie's
   * slices; `buildDailySeries` zero-fills days a value saw no usage so the
   * lines never interpolate over gaps.
   */
  const renderDailySeries = (
    dataAsOf: string,
    entries: DailyEntry[] | null
  ) => {
    if (entries === null) return null; // older backend (rolling deploy)
    const series = buildDailySeries(
      entries,
      windowStartDay(dataAsOf, windowDays),
      dataAsOf
    );
    if (series.labels.length === 0) return null;
    const dimensionLabel = DIMENSION_LABELS[dimension];
    const typeFiltered = dimension === "operation" && operationType !== "all";
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Operations per day by {dimensionLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          One line per {dimensionLabel} for the top 10 by total over the
          selected window
          {typeFiltered
            ? `, showing only ${
                operationType === "query" ? "queries" : "mutations"
              } (the combined Other line is drawn under All only).`
            : "; the remaining values are combined into Other."}
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={series.rows}
            margin={{ top: 16, right: 24, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} minTickGap={24} />
            <YAxis allowDecimals={false} width={44} tick={{ fontSize: 11 }} />
            {/* Hovering a day lists every line, largest count first. */}
            <Tooltip
              itemSorter={(item) =>
                typeof item.value === "number" ? -item.value : 0
              }
            />
            <Legend />
            {series.labels.map((label, index) => (
              <Line
                key={label}
                type="monotone"
                dataKey={label}
                name={label}
                stroke={SLICE_COLORS[index % SLICE_COLORS.length]}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Paper>
    );
  };

  const renderReport = () => {
    if (!report) return null;
    if (report.dataAsOf === null) {
      // Req 1.8: the no-data-yet indication replaces the Data_As_Of_Date;
      // controls above stay available.
      return (
        <Alert severity="info">
          No aggregated usage data is available yet.
        </Alert>
      );
    }
    // The Operation view filters client-side by the type sub-selection (the
    // pie's entries and the daily lines alike); every other dimension
    // charts the retrieved data as-is.
    const chartEntries =
      dimension === "operation"
        ? filterOperationEntries(report.entries, operationType)
        : report.entries;
    const dailyEntries =
      dimension === "operation" && report.dailyEntries !== null
        ? filterOperationEntries(report.dailyEntries, operationType)
        : report.dailyEntries;
    return (
      <>
        {/* Req 1.5: the data-as-of date on the same screen as the report. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Data as of {report.dataAsOf} (UTC)
        </Typography>
        {chartEntries.length === 0 ? (
          // Req 2.6: empty state without a chart; controls stay available.
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No usage data matches the current selections.
          </Typography>
        ) : (
          renderChart(chartEntries)
        )}
        {/* The trend belongs with the user slicing (unique people per day);
            every other dimension gets one operations-per-day line per value. */}
        {dimension === "user"
          ? renderTrend(report.dataAsOf, report.dailyDistinctUsers)
          : renderDailySeries(report.dataAsOf, dailyEntries)}
      </>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Selection controls (Req 3.1, 3.2): exclusive groups always holding
          one indicated selection; interactive during loading and after
          generic errors (Req 1.7, 3.7). */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Slice by
            </Typography>
            <ToggleButtonGroup
              value={dimension}
              exclusive
              size="small"
              onChange={handleDimensionChange}
              aria-label="Usage dimension"
            >
              <ToggleButton value="product">Product</ToggleButton>
              <ToggleButton value="user">User</ToggleButton>
              <ToggleButton value="team">Team</ToggleButton>
              <ToggleButton value="operation">Operation</ToggleButton>
              <ToggleButton value="channel">Channel</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {dimension === "operation" && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Type
              </Typography>
              <ToggleButtonGroup
                value={operationType}
                exclusive
                size="small"
                onChange={handleOperationTypeChange}
                aria-label="Operation type"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="query">Queries</ToggleButton>
                <ToggleButton value="mutation">Mutations</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Window
            </Typography>
            <ToggleButtonGroup
              value={windowDays}
              exclusive
              size="small"
              onChange={handleWindowChange}
              aria-label="Reporting window"
            >
              <ToggleButton value={7}>7 days</ToggleButton>
              <ToggleButton value={30}>30 days</ToggleButton>
              <ToggleButton value={90}>90 days</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Paper>

      {status === "loading" ? (
        // Req 1.4: loading indicator while a retrieval is in progress.
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : status === "error" ? (
        // Req 1.7 / 1.9: retrieval failed (including timeout); the message
        // replaces the chart and the controls above remain for retry.
        <Alert severity="error">
          Failed to retrieve usage data. Change a selection to retry.
        </Alert>
      ) : (
        renderReport()
      )}
    </Box>
  );
};

export default UsageReportViewer;
